import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  PhoneIcon,
  PhoneArrowUpRightIcon,
  PhoneArrowDownLeftIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const CallHistory = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cdrs, setCdrs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    direction: '',
    disposition: '',
    startDate: '',
    endDate: '',
    caller: '',
    callee: '',
    minDuration: '',
    maxDuration: ''
  });
  const [exporting, setExporting] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchCDRs();
    fetchStats();
  }, [pagination.page, filters]);

  const fetchCDRs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      if (filters.direction) params.append('direction', filters.direction);
      if (filters.disposition) params.append('disposition', filters.disposition);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.caller) params.append('caller', filters.caller);
      if (filters.callee) params.append('callee', filters.callee);
      if (filters.minDuration) params.append('minDuration', filters.minDuration);
      if (filters.maxDuration) params.append('maxDuration', filters.maxDuration);

      const response = await axiosInstance.get(`/api/sip/cdrs?${params.toString()}`);
      if (response.data.success) {
        // Handle both response formats: response.data.data or response.data.cdrs
        const cdrData = response.data.data || response.data.cdrs || [];
        setCdrs(Array.isArray(cdrData) ? cdrData : []);
        setPagination({
          ...pagination,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        });
      } else {
        setCdrs([]);
      }
    } catch (error) {
      logger.error('Error fetching CDRs:', error);
      showNotification?.('Failed to load call history', 'error');
      setCdrs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await axiosInstance.get(`/api/sip/cdrs/stats/summary?${params.toString()}`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const fetchCallDetail = async (cdrId) => {
    try {
      setLoadingDetail(true);
      const response = await axiosInstance.get(`/api/sip/cdrs/${cdrId}`);
      if (response.data.success) {
        setSelectedCall(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching call detail:', error);
      showNotification?.('Failed to load call details', 'error');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.append('format', 'csv');
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.disposition) params.append('disposition', filters.disposition);

      const response = await axiosInstance.get(`/api/sip/cdrs/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `call-history-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification?.('Export completed successfully', 'success');
    } catch (error) {
      logger.error('Error exporting CDRs:', error);
      showNotification?.('Failed to export call history', 'error');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      direction: '',
      disposition: '',
      startDate: '',
      endDate: '',
      caller: '',
      callee: '',
      minDuration: '',
      maxDuration: ''
    });
    setPagination({ ...pagination, page: 1 });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const formatTimestamp = (ms) => {
    if (!ms && ms !== 0) return '';
    const totalSecs = Math.floor(ms / 1000);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDispositionBadge = (disposition) => {
    const dispositionConfig = {
      ANSWERED: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircleIcon },
      NO_ANSWER: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: ClockIcon },
      BUSY: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: ExclamationCircleIcon },
      FAILED: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircleIcon },
      CANCEL: { color: 'bg-gray-100 text-gray-700 dark:bg-[#1a1a1a] dark:text-gray-400', icon: XCircleIcon }
    };

    const config = dispositionConfig[disposition] || dispositionConfig.FAILED;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        <Icon className="w-3 h-3" />
        {disposition}
      </span>
    );
  };

  const getDirectionIcon = (direction) => {
    if (direction === 'inbound') {
      return <PhoneArrowDownLeftIcon className="w-4 h-4 text-green-600 dark:text-green-400" />;
    }
    return <PhoneArrowUpRightIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
  };

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto relative">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/voice')}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Call History</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
                showFilters
                  ? 'border-orange-500 text-orange-600 bg-orange-50 dark:bg-orange-900/20'
                  : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
              }`}
            >
              <FunnelIcon className="w-3.5 h-3.5" />
              Filters
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Calls</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {(stats.totalCalls || 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <PhoneIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Answered</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {(stats.answeredCalls || 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Minutes</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {Math.round((stats.totalTalkTime || 0) / 60).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <ClockIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Answer Rate</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.answerRate || 0}%
                  </p>
                </div>
                <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <PhoneArrowUpRightIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-gray-50 dark:bg-[#1a1a1a] rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Filter Calls
              </h3>
              <button
                onClick={clearFilters}
                className="text-sm text-orange-600 dark:text-orange-400 hover:underline"
              >
                Clear all
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Direction
                </label>
                <select
                  value={filters.direction}
                  onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                >
                  <option value="">All</option>
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Outbound</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Disposition
                </label>
                <select
                  value={filters.disposition}
                  onChange={(e) => setFilters({ ...filters, disposition: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                >
                  <option value="">All</option>
                  <option value="ANSWERED">Answered</option>
                  <option value="NO_ANSWER">No Answer</option>
                  <option value="BUSY">Busy</option>
                  <option value="FAILED">Failed</option>
                  <option value="CANCEL">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Caller Number
                </label>
                <input
                  type="text"
                  value={filters.caller}
                  onChange={(e) => setFilters({ ...filters, caller: e.target.value })}
                  placeholder="e.g., +1234..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Called Number
                </label>
                <input
                  type="text"
                  value={filters.callee}
                  onChange={(e) => setFilters({ ...filters, callee: e.target.value })}
                  placeholder="e.g., +1234..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Min Duration (sec)
                </label>
                <input
                  type="number"
                  value={filters.minDuration}
                  onChange={(e) => setFilters({ ...filters, minDuration: e.target.value })}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Duration (sec)
                </label>
                <input
                  type="number"
                  value={filters.maxDuration}
                  onChange={(e) => setFilters({ ...filters, maxDuration: e.target.value })}
                  placeholder="3600"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
            </div>
            <button
              onClick={() => {
                setPagination({ ...pagination, page: 1 });
                fetchCDRs();
                fetchStats();
              }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
            >
              <MagnifyingGlassIcon className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        )}

        {/* CDR Table */}
        <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
            </div>
          ) : cdrs.length === 0 ? (
            <div className="text-center py-12">
              <PhoneIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No calls found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {Object.values(filters).some(v => v) ? 'Try adjusting your filters' : 'Call records will appear here'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-[#222222]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Direction
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date/Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        From
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        To
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cost
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#1a1a1a] divide-y divide-gray-200 dark:divide-gray-800">
                    {cdrs.map((cdr) => (
                      <tr
                        key={cdr._id}
                        onClick={() => fetchCallDetail(cdr._id)}
                        className="hover:bg-gray-50 dark:hover:bg-[#222222]/50 cursor-pointer"
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getDirectionIcon(cdr.direction)}
                            <span className="text-sm text-gray-900 dark:text-white capitalize">
                              {cdr.direction}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 dark:text-white">
                            {formatDateTime(cdr.timestamps?.inviteReceived || cdr.createdAt)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatPhoneNumber(cdr.caller?.number)}
                            </p>
                            {cdr.caller?.name && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {cdr.caller.name}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {cdr.agentName && (
                              <div className="w-5 h-5 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                                <span className="text-[8px] font-bold text-orange-600 dark:text-orange-400">AI</span>
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {cdr.agentName || formatPhoneNumber(cdr.callee?.number)}
                              </p>
                              {cdr.agentName && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatPhoneNumber(cdr.callee?.number)}
                                </p>
                              )}
                              {!cdr.agentName && cdr.callee?.name && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {cdr.callee.name}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 dark:text-white">
                            {formatDuration(cdr.duration?.talking || cdr.duration?.total || 0)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getDispositionBadge(cdr.disposition)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 dark:text-white">
                            ${(cdr.billing?.totalCost || 0).toFixed(4)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {cdr.recording?.url && (
                            <button
                              onClick={(e) => { e.stopPropagation(); window.open(cdr.recording.url, '_blank'); }}
                              className="p-1 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
                              title="Play recording"
                            >
                              <PlayIcon className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page >= pagination.pages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Call Detail Slide-over Panel */}
      {(selectedCall || loadingDetail) && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => { setSelectedCall(null); setLoadingDetail(false); }}
          />

          {/* Panel */}
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white dark:bg-[#1a1a1a] shadow-xl z-50 overflow-y-auto">
            {loadingDetail && !selectedCall ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              </div>
            ) : selectedCall && (
              <>
                {/* Panel Header */}
                <div className="sticky top-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between z-10">
                  <div className="flex items-center gap-3">
                    <PhoneIcon className="w-5 h-5 text-orange-600" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Call Details
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedCall(null)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="px-6 py-6 space-y-6">
                  {/* Direction & Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getDirectionIcon(selectedCall.direction)}
                      <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                        {selectedCall.direction} Call
                      </span>
                    </div>
                    {getDispositionBadge(selectedCall.disposition)}
                  </div>

                  {/* Agent Info */}
                  {selectedCall.agentName && (
                    <div className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                        <span className="text-xs font-bold text-orange-600 dark:text-orange-400">AI</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-orange-700 dark:text-orange-300">
                          AI Agent: {selectedCall.agentName}
                        </p>
                        {selectedCall.callContext?.agentDepartment && (
                          <p className="text-xs text-orange-600 dark:text-orange-400">
                            {selectedCall.callContext.agentDepartment}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* From / To */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">From</p>
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatPhoneNumber(selectedCall.caller?.number)}
                          </p>
                          {(selectedCall.caller?.name || selectedCall.callContext?.contactName) && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {selectedCall.caller?.name || selectedCall.callContext?.contactName}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">To</p>
                      <div className="flex items-center gap-2">
                        {selectedCall.agentName ? (
                          <div className="w-5 h-5 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                            <span className="text-[8px] font-bold text-orange-600 dark:text-orange-400">AI</span>
                          </div>
                        ) : (
                          <UserIcon className="w-4 h-4 text-gray-400" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedCall.agentName || formatPhoneNumber(selectedCall.callee?.number)}
                          </p>
                          {selectedCall.agentName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatPhoneNumber(selectedCall.callee?.number)}
                            </p>
                          )}
                          {!selectedCall.agentName && selectedCall.callee?.name && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {selectedCall.callee.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Time Details */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <ClockIcon className="w-4 h-4" />
                      Time Details
                    </h3>
                    <div className="bg-gray-50 dark:bg-[#222222] rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                      <div className="flex justify-between px-4 py-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Started</span>
                        <span className="text-sm text-gray-900 dark:text-white">
                          {formatDateTime(selectedCall.timestamps?.inviteReceived || selectedCall.createdAt)}
                        </span>
                      </div>
                      {selectedCall.timestamps?.answered && (
                        <div className="flex justify-between px-4 py-3">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Answered</span>
                          <span className="text-sm text-gray-900 dark:text-white">
                            {formatDateTime(selectedCall.timestamps.answered)}
                          </span>
                        </div>
                      )}
                      {selectedCall.timestamps?.ended && (
                        <div className="flex justify-between px-4 py-3">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Ended</span>
                          <span className="text-sm text-gray-900 dark:text-white">
                            {formatDateTime(selectedCall.timestamps.ended)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between px-4 py-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Duration</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDuration(selectedCall.duration?.talking || selectedCall.duration?.total || 0)}
                        </span>
                      </div>
                      {selectedCall.billing?.totalCost > 0 && (
                        <div className="flex justify-between px-4 py-3">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Cost</span>
                          <span className="text-sm text-gray-900 dark:text-white">
                            ${(selectedCall.billing.totalCost).toFixed(4)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Call Outcome Details */}
                  {(selectedCall.sentimentScore !== null || selectedCall.hangupCause || selectedCall.voiceCallStatus) && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <ExclamationCircleIcon className="w-4 h-4" />
                        Call Details
                      </h3>
                      <div className="bg-gray-50 dark:bg-[#222222] rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                        {selectedCall.sentimentScore !== null && selectedCall.sentimentScore !== undefined && (
                          <div className="flex justify-between px-4 py-3">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Sentiment</span>
                            <span className={`text-sm font-medium ${
                              selectedCall.sentimentScore > 0.3 ? 'text-green-600 dark:text-green-400' :
                              selectedCall.sentimentScore < -0.3 ? 'text-red-600 dark:text-red-400' :
                              'text-gray-600 dark:text-gray-400'
                            }`}>
                              {selectedCall.sentimentScore > 0.3 ? 'Positive' :
                               selectedCall.sentimentScore < -0.3 ? 'Negative' : 'Neutral'}
                              {' '}({(selectedCall.sentimentScore * 100).toFixed(0)}%)
                            </span>
                          </div>
                        )}
                        {selectedCall.hangupCause && (
                          <div className="flex justify-between px-4 py-3">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Hangup Cause</span>
                            <span className="text-sm text-gray-900 dark:text-white">{selectedCall.hangupCause}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recording */}
                  {(selectedCall.recordingUrl || selectedCall.recording?.url) && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <PlayIcon className="w-4 h-4" />
                        Recording
                      </h3>
                      <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                        <audio controls className="w-full" src={selectedCall.recordingUrl || selectedCall.recording?.url}>
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    </div>
                  )}

                  {/* Transcript */}
                  {selectedCall.transcript && selectedCall.transcript.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                        Transcript ({selectedCall.transcript.length} messages)
                      </h3>
                      <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4 space-y-3 max-h-96 overflow-y-auto">
                        {selectedCall.transcript.map((entry, idx) => (
                          <div
                            key={idx}
                            className={`flex ${entry.speaker === 'agent' ? 'justify-start' : 'justify-end'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg px-3 py-2 ${
                                entry.speaker === 'agent'
                                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100'
                                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold">
                                  {entry.speaker === 'agent' ? (selectedCall.agentName || 'Agent') : 'Caller'}
                                </span>
                                {entry.timestamp !== undefined && (
                                  <span className="text-xs opacity-60">
                                    {formatTimestamp(entry.timestamp)}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm">{entry.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Flat transcription text (from CDR) when no structured transcript */}
                  {(!selectedCall.transcript || selectedCall.transcript.length === 0) && selectedCall.transcription?.text && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                        Transcription
                      </h3>
                      <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4 max-h-96 overflow-y-auto">
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {selectedCall.transcription.text}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* No transcript message */}
                  {(!selectedCall.transcript || selectedCall.transcript.length === 0) && !selectedCall.transcription?.text && (
                    <div className="text-center py-6 bg-gray-50 dark:bg-[#222222] rounded-lg">
                      <ChatBubbleLeftRightIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        No transcript available for this call
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CallHistory;
